import React, { useContext, useEffect, useMemo, useState } from "react";
import { Container, Button, Spinner } from "reactstrap";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import Reason, { ReasonSubmitPayload } from "common/utilities/Reason/Reason";
import formStyles from "../../../../assets/scss/common/formStyle.module.scss";
import { useDisposalService } from "modules/Pharmacy/Disposal/api/useDisposal";
import { showToastMessage } from "../../../../common/service/CommonService";
import CommonForm, { FormFieldConfig } from "Components/Common/CommonForm";
import { DISPOSAL_PATHS } from "Routes/paths";
import { DisposalFormValues } from "modules/Pharmacy/Disposal/helpers/disposal.types";
import FullPageLoader from "Components/Common/FullPageLoader";
import { validations } from "validations/FormValidations";
import { useDispensingLog } from "modules/Pharmacy/Dispensing/api/useDispensingLog";
import { useIpInventoryService } from "modules/Pharmacy/IPinventory/api/useIpInventoryService";
import {
  DISPOSAL_LOCATION_OPTIONS,
  DISPOSAL_METHOD_OPTIONS,
  DISPOSAL_REASON_OPTIONS,
  loadCodelistsDetailId
} from "api/modules/common.option";

import SectionCard from "Components/Common/SectionCard";
import { BreadcrumbContext } from "Layouts/Header";
import { useGlobalStudy } from "context/StudyContext";
import GlobalStudyMismatchWarning from "../../../../Components/Common/GlobalStudyMismatchWarning";
import { queryClient } from "api/queryClient";

const AddDisposal: React.FC = () => {
  const navigate = useNavigate();
  const breadcrumbContext = useContext(BreadcrumbContext);
  const { id: paramId } = useParams();
  const [searchParams] = useSearchParams();
  const editId = paramId || searchParams.get("id");

  document.title = `${editId ? "Edit" : "Add"} Disposal Record | ZENTIXS ORBIT`;

  useEffect(() => {
    breadcrumbContext?.setBreadcrumbItems?.([
      { text: "Drug Destruction", link: DISPOSAL_PATHS.LIST },
      { text: editId ? "Edit Disposal Record" : "Add Disposal Record" },
    ]);

    return () => breadcrumbContext?.setBreadcrumbItems?.([]);
  }, [editId]);

  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);

  const { InsertMutation, UpdateMutation, SelectIdQuery } = useDisposalService();
  const selectQuery = SelectIdQuery(editId ? Number(editId) : null);

  const { DropdownQuery: studySelectAllQuery } = useDispensingLog({ getStudyDropDown: true });
  const studyOptions = useMemo(
    () =>
      (studySelectAllQuery.data as any)?.data?.map((study: any) => ({
        label: study.studycode,
        value: study.studyid,
      })) || [],
    [studySelectAllQuery.data],
  );

  useEffect(() => {
    loadCodelistsDetailId([
      "DISPOSAL_LOCATION_OPTIONS",
      "DISPOSAL_METHOD_OPTIONS",
      "DISPOSAL_REASON_OPTIONS"
    ]);
  }, []);

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm<DisposalFormValues>({
    defaultValues: {
      studycode: "",
      sponsorname: "",
      sponsorcode: "",
      ipid: "",
      ipcategory: "Test Product",
      productname: "",
      batchlotno: "",
      treatmentcode: "",
      dosageform: "",
      strength: "",
      expirydate: "",
      availablequantity: "",
      retainedquantity: "",
      disposalquantity: "",
      disposalreason: "",
      disposalmethod: "",
      disposaldate: "",
      disposallocation: "On-site",
      disposalvendor: "",
      quantitytype: ""
    },
    mode: "onBlur",
  });


  const { selectedStudy: globalStudy } = useGlobalStudy();

  useEffect(() => {
    if (editId) return;
    if (globalStudy?.studyid && studyOptions.length > 0) {
      const matched = studyOptions.find(
        (s: any) => String(s.value) === String(globalStudy.studyid)
      );
      if (matched) {
        setValue("studycode", matched.value, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    } else {
      setValue("studycode", "", {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [globalStudy?.studyid, studyOptions, editId, setValue]);

  const selectedStudyCodeId = watch("studycode");
  const selectedStudy = useMemo(
    () => studyOptions.find((s: any) => s.value === selectedStudyCodeId),
    [selectedStudyCodeId, studyOptions],
  );

  useEffect(() => {
    // When Study Code is cleared
    if (!selectedStudyCodeId && !editId) {

      // Clear dependent fields
      setValue("ipcategory", "");
      setValue("ipid", "");
      setValue("sponsorname", "");
      setValue("sponsorcode", "");
      setValue("productname", "");
      setValue("batchlotno", "");
      setValue("dosageform", "");
      setValue("strength", "");
      setValue("expirydate", "");
      setValue("quantitytype", "");
      setValue("totalquantity", "");

      // Reset quantities
      setTotalQuantityVal(0);
      setRetentionQuantityVal(0);
      setReturnedStockVal(0);

      // Optional: reset category options
      setIPCategoryOptions([]);
    }
  }, [selectedStudyCodeId, setValue, editId]);

  const { SelectIpIDQuery: ipSelectQuery } = useIpInventoryService();
  const ipQuery = ipSelectQuery(selectedStudy?.value || null);

  const ipOptions = useMemo(
    () =>
      (ipQuery.data as any)?.data?.map((ip: any) => ({
        label: `${ip.batchlotno} (${ip.productname})`,
        value: ip.ipid,
        ...ip,
      })) || [],
    [ipQuery.data],
  );

  const IP_CATEGORY_OPTIONS = [
    { label: "Test Product", value: "Test Product" },
    { label: "Reference Product", value: "Reference Product" },
    { label: "Placebo", value: "Placebo" },
    { label: "Ancillary / Supply", value: "Ancillary / Supply" },
  ];

  const [IPCategoryOptions, setIPCategoryOptions] = useState<any[]>(IP_CATEGORY_OPTIONS);

  const [totalquantityVal, setTotalQuantityVal] = useState<any>()
  const [retentionquantityVal, setRetentionQuantityVal] = useState<any>()
  const [returnedStockVal, setReturnedStockVal] = useState<any>()

  useEffect(() => {
    if (!ipOptions || ipOptions.length === 0) return;
    if (ipOptions.length > 0) {
      const uniqueCategories = ipOptions.map((ip: any) => ip.ipcategory);
      const updatedIPCategoryOptions = uniqueCategories.map((category: string) => ({
        label: category,
        value: category,
      }));
      setIPCategoryOptions(updatedIPCategoryOptions);
    }
  }, [ipOptions]);

  useEffect(() => {
    if (editId) return;
    if (!selectedStudy) return;
    if (!ipOptions.length) return;

    // get category from first IP
    const firstCategory = ipOptions[0]?.ipcategory;

    if (!firstCategory) return;

    setValue("ipcategory", firstCategory, {
      shouldValidate: true,
      shouldDirty: true,
    });

  }, [selectedStudy, ipOptions, editId, setValue]);

  useEffect(() => {
    if (selectQuery.data?.data?.[0]) {
      const record = selectQuery.data.data[0];
      reset({
        ...record,
        totalquantity: record?.totalquantity,
        quantitytype: record?.quantitytype,
        studycode: record?.studyid,
        disposalreason: record?.disposalreason,
        disposalmethod: record?.disposalmethod,
        disposaldate: record.disposaldate ? record.disposaldate.split("T")[0] : "",
        expirydate: record.expirydate ? record.expirydate.split("T")[0] : "",
      });
    }
  }, [selectQuery.data, reset]);

  const selectedIPCategory = watch("ipcategory");

  useEffect(() => {
    if (!selectedIPCategory) {
      if (!editId) {
        setValue("ipid", "")
        setValue("sponsorname", "");
        setValue("sponsorcode", "");
        setValue("productname", "");
        setValue("batchlotno", "");
        setValue("dosageform", "");
        setValue("strength", "");
        setValue("expirydate", "");
        setTotalQuantityVal(0);
        setRetentionQuantityVal(0);
        setReturnedStockVal(0);
      }
      return;
    }

    const matchingIP = ipOptions.find((ip: any) => ip.ipcategory === selectedIPCategory);

    if (matchingIP) {
      setValue("ipid", matchingIP.ipid ?? "");
      setValue("sponsorname", matchingIP.sponsorname ?? "");
      setValue("sponsorcode", matchingIP.sponsorcode ?? "");
      setValue("productname", matchingIP.productname ?? "");
      setValue("batchlotno", matchingIP.batchlotno ?? "");
      setValue("dosageform", matchingIP.dosageform ?? "");
      setValue("strength", matchingIP.strength ?? "");
      setValue("expirydate", matchingIP.expirydate ? matchingIP.expirydate.split("T")[0] : "");
      setTotalQuantityVal(Number(matchingIP.availablequantity || 0));
      setRetentionQuantityVal(Number(matchingIP.retentionquantity ?? 0));
      setReturnedStockVal(Number(matchingIP.returnedquantity ?? 0));
    } else if (!editId) {
      setTotalQuantityVal(0);
      setRetentionQuantityVal(0);
      setReturnedStockVal(0);
    }
  }, [selectedIPCategory, ipOptions, setValue, editId]);

  const quantitytype = watch("quantitytype");

  useEffect(() => {
    if (!quantitytype) {
      setValue("totalquantity", "");
      return;
    }

    const selectedType = QUANTITY_TYPE_OPTIONS.find(opt => opt.value === quantitytype);
    const typeLabel = selectedType?.label?.toString().toLowerCase() || "";

    if (typeLabel.includes("actual") && typeLabel.includes("stock")) {
      setValue("totalquantity", totalquantityVal || 0);

    } else if (typeLabel.includes("retention") && typeLabel.includes("stock")) {
      setValue("totalquantity", retentionquantityVal || 0);
    } else if (typeLabel.includes("returned") && typeLabel.includes("stock")) {
      setValue("totalquantity", returnedStockVal || 0);
    } else {
      setValue("totalquantity", "");
    }
  }, [quantitytype, setValue, totalquantityVal, retentionquantityVal, returnedStockVal]);


  const QUANTITY_TYPE_OPTIONS = [
    { label: "Actual Stock", value: "Actual Stock" },
    { label: "Retention Stock", value: "Retention Stock" },
    { label: "Returned Stock", value: "Returned Stock" },
  ]

  // Section 1: Study & Product Details (Row 1)
  const section1FieldsRow1: FormFieldConfig[] = [
    {
      name: "studycode",
      label: "Study Code",
      type: "select",
      required: true,
      options: studyOptions,
      disabled: true,
      placeholder: "Select study code...",
      rules: validations.drugRetention.studyid,
    },
    {
      name: "sponsorname",
      label: "Sponsor Name",
      type: "text",
      placeholder: "Auto-filled",
      disabled: true,
    },
  ];

  // Section 1: Study & Product Details (Row 2 - IP Category)
  const section1FieldsRow2: FormFieldConfig[] = [
    {
      name: "ipcategory",
      label: "IP Category",
      type: "radio",
      required: true,
      disabled: !selectedStudyCodeId || !!editId,
      options: IPCategoryOptions,
      rules: validations.ipInventory.ipcategory,
    },
  ];

  // Section 1: Study & Product Details (Row 3 - Drug Details)
  const section1FieldsRow3: FormFieldConfig[] = [
    {
      name: "productname",
      label: "Product Name",
      type: "text",
      disabled: true,
      placeholder: "Enter product name as per label...",
    },
    {
      name: "batchlotno",
      label: "Batch / Lot No.",
      type: "text",
      disabled: true,
      placeholder: "Enter batch number...",
    },
    {
      name: "dosageform",
      label: "Dosage Form",
      type: "text",
      disabled: true,
      placeholder: "e.g. Tablet, Capsule",
    },
  ];

  // Section 1: Study & Product Details (Row 4 - Strength & Expiry)
  const section1FieldsRow4: FormFieldConfig[] = [
    {
      name: "strength",
      label: "Strength",
      type: "text",
      disabled: true,
      placeholder: "e.g. 50mg",
    },
    {
      name: "expirydate",
      label: "Expiry Date",
      type: "text",
      disabled: true,
      placeholder: "Auto-filled",
    },
  ];

  const sectionCFields: FormFieldConfig[] = [
    {
      name: "quantitytype",
      label: "Quantity Type",
      type: "select",
      required: true,
      disabled: !selectedIPCategory,
      options: QUANTITY_TYPE_OPTIONS,
    },
    {
      name: "totalquantity",
      label: "Quantity Available for Disposal",
      type: "number",
      disabled: true
    },
    {
      name: "disposalquantity",
      label: "Quantity for Disposal",
      type: "number",
      required: true,
      placeholder: "e.g. 400",
      rules: {
        required: "Quantity for disposal is required",
        validate: (value: number) => {
          const total = Number(watch("totalquantity")) || 0;
          if (value > total) return "Quantity cannot exceed available stock";
          if (value <= 0) return "Quantity must be greater than 0";
          return true;
        },
      },
    },
    {
      name: "disposalreason",
      label: "Reason for Disposal",
      type: "select",
      required: true,
      options: DISPOSAL_REASON_OPTIONS,
    },
    {
      name: "disposalmethod",
      label: "Disposal Method",
      type: "select",
      required: true,
      options: DISPOSAL_METHOD_OPTIONS,
    },
  ];

  const sectionFFields: FormFieldConfig[] = [
    {
      name: "disposaldate",
      label: "Disposal Date",
      type: "date",
      required: true,
    },
    {
      name: "disposallocation",
      label: "Disposal Location",
      type: "select",
      options: DISPOSAL_LOCATION_OPTIONS,
      required: true,
    },
    {
      name: "disposalvendor",
      label: "Disposal Vendor (if external)",
      type: "text",
      placeholder: "e.g. CleanWaste Inc.",
    },
  ];

  const onSubmit = (data: any) => {

    const insertpayload = {
      ipid: data?.ipid,
      totalquantity: data?.totalquantity,
      quantitytype: data?.quantitytype,
      disposalquantity: data?.disposalquantity,
      disposalreason: data?.disposalreason,
      disposalmethod: data?.disposalmethod,
      disposaldate: data?.disposaldate,
      disposallocation: data?.disposallocation,
      disposalvendor: data?.disposalvendor,
    }
    if (editId) {
      setIsUpdateModalOpen(true);
    } else {
      InsertMutation.mutate(insertpayload, {
        onSuccess: (res: any) => {
          queryClient.invalidateQueries({ queryKey: ["verification-notification-count"] });
          showToastMessage("success", res.message);
          navigate(DISPOSAL_PATHS.LIST);
        },
        onError: (err: any) => showToastMessage("error", err),
      });
    }
  };

  const handleUpdate = (reasonPayload: ReasonSubmitPayload) => {
    const formData = watch();

    const updatepayload = {
      disposalid: Number(editId),
      quantitytype: formData?.quantitytype,
      totalquantity: formData?.totalquantity,
      disposalquantity: formData?.disposalquantity,
      disposalreason: formData?.disposalreason,
      disposalmethod: formData?.disposalmethod,
      disposaldate: formData?.disposaldate,
      disposallocation: formData?.disposallocation,
      disposalvendor: formData?.disposalvendor,
      updatereason: reasonPayload.reason,
    }

    UpdateMutation.mutate(updatepayload, {
      onSuccess: (res: any) => {
        showToastMessage("success", res.message);
        setIsUpdateModalOpen(false);
        navigate(DISPOSAL_PATHS.LIST);
      },
      onError: (err: any) => showToastMessage("error", err),
    });
  };

  if (editId && (selectQuery.isLoading || selectQuery.isFetching)) return <FullPageLoader />;

  return (
    <div className={`page-content ${formStyles.pageContainer}`}>
      <Container fluid>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h3 className="fw-700 mb-1">Drug Destruction & Disposal Protocol</h3>
              <p className="text-muted mb-0">Complete this protocol to authorize and document the disposal of clinical trial materials.</p>
            </div>
          </div>

          {/* Study Mismatch Warning */}
          <GlobalStudyMismatchWarning
            globalStudy={globalStudy}
            studyOptions={studyOptions}
            isLoaded={!studySelectAllQuery.isLoading}
          />

          <SectionCard
            index={1}
            icon="ri-shield-check-line"
            title="Study & Product Details"
            subtitle="Select the study protocol and enter product information."
          >
            <CommonForm fields={section1FieldsRow1} control={control} errors={errors} columns={3} />
            <div className="mt-3">
              <CommonForm fields={section1FieldsRow2} control={control} errors={errors} columns={1} />
            </div>
            <div className="mt-3">
              <CommonForm fields={section1FieldsRow3} control={control} errors={errors} columns={3} />
            </div>
            <div className="mt-3">
              <CommonForm fields={section1FieldsRow4} control={control} errors={errors} columns={3} />
            </div>
          </SectionCard>

          <SectionCard
            index={2}
            icon="ri-medicine-bottle-line"
            title="Disposal Drug Details"
            subtitle="Specify disposal quantities and other details."
          >
            <CommonForm fields={sectionCFields} control={control} errors={errors} columns={3} />
          </SectionCard>

          <SectionCard
            index={3}
            icon="ri-calendar-event-line"
            title="Execution Details"
            subtitle="Final disposal execution information."
          >
            <CommonForm fields={sectionFFields} control={control} errors={errors} columns={3} />
          </SectionCard>

          <div className={formStyles.stickyFooter}>
            <div className={formStyles.footerActions}>
              <Button
                type="button"
                color="dark"
                outline
                onClick={() => navigate(DISPOSAL_PATHS.LIST)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                color="primary"
                disabled={InsertMutation.isPending || UpdateMutation.isPending}
              >
                {InsertMutation.isPending && <Spinner size="sm" className="me-2" />}
                {editId ? "Update" : "Submit"}
              </Button>
            </div>
          </div>
        </form>

        {isUpdateModalOpen && (
          <Reason
            isOpen={isUpdateModalOpen}
            toggle={() => setIsUpdateModalOpen(false)}
            click={handleUpdate}
            action="Update"
            id={editId ? Number(editId) : null}
            isLoading={UpdateMutation.isPending}
          />
        )}
      </Container>
    </div>
  );
};

export default AddDisposal;
